import { ReactNode } from "react";
import withLayout from "../utils/withLayout";
import NotFound from "../pages/ErrorPages/NotFound";
import Forbidden from "../pages/ErrorPages/Forbidden";
import Home from "../pages";
import UsersPage from "../pages/users";
import ClientsPage from "../pages/clients";
import ParticipantProfilePage from "../pages/clients/profile";
import GoalsPage from "../pages/clients/profile/goals";
import rewardsConfigPage from "../pages/rewardsConfiguration";
import RewardsPage from "@/pages/clients/profile/rewards";
import DashboardPage from "@/pages/dashboard";
import JournalsPage from "@/pages/clients/profile/journals";
import MessagesPage from "@/pages/chat/";
import ScreeningColorsPage from "@/pages/screeningColors";
import SettingsPage from "@/pages/settings";
import OrganizationsPage from "../pages/organizations";
import OrganizationLabelsPage from "../pages/organizationLabels";
import ActionsPage from "../pages/actions";
import RolesPage from "../pages/roles";
import EducationalResourcesPage from "../pages/educationalResources";
import CategoriesPage from "../pages/categories";
import ProgramsPage from "../pages/programs";
import ExternalProvidersPage from "../pages/externalProviders";
import GoalTemplatesPage from "../pages/goalTemplates";
import TermsPage from "../pages/terms";
import SettingsCatalogPage from "../pages/settingsCatalog";
import AccountSettingsPage from "../pages/accountSettings";

export interface RouteItem {
  path: string; // Path of the route
  element?: ReactNode; // Element to render for this route (optional for parent routes with children)
  requiresAuth?: boolean; // Whether authentication is required
  requiredActions?: string[]; // Required actions to access the route
  children?: RouteItem[]; // Nested routes (children)
}

//TODO: we should extract the known routes from the db so they are dynamic
export const routesConfig = [
  {
    path: "/",
    element: withLayout(Home)({}),
    requiresAuth: false,
    requiredActions: ["DASHBOARD_VIEW"],
  },
  {
    path: "/participants",
    element: withLayout(ClientsPage)({}),
    requiredActions: ["PARTICIPANTS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId",
    element: withLayout(ParticipantProfilePage)({}),
    requiredActions: ["PARTICIPANTS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId/goals",
    element: withLayout(GoalsPage)({}),
    requiredActions: ["PARTICIPANT_GOALS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/users",
    element: withLayout(UsersPage)({}),
    requiredActions: ["USERS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/orgUnits",
    element: withLayout(OrganizationsPage)({}),
    requiredActions: ["ORG_UNITS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/organization-labels",
    element: withLayout(OrganizationLabelsPage)({}),
    requiredActions: ["ORGANIZATION_LABELS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/actions",
    element: withLayout(ActionsPage)({}),
    requiredActions: ["ACTIONS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/roles",
    element: withLayout(RolesPage)({}),
    requiredActions: ["ROLES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/educational-resources",
    element: withLayout(EducationalResourcesPage)({}),
    requiredActions: ["EDUCATIONAL_RESOURCES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/categories",
    element: withLayout(CategoriesPage)({}),
    requiredActions: ["CATEGORIES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/programs",
    element: withLayout(ProgramsPage)({}),
    requiredActions: ["PROGRAMS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/external-providers",
    element: withLayout(ExternalProvidersPage)({}),
    requiredActions: ["EXTERNAL_PROVIDERS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/goal-templates",
    element: withLayout(GoalTemplatesPage)({}),
    requiredActions: ["GOAL_TEMPLATES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/terms",
    element: withLayout(TermsPage)({}),
    requiredActions: ["TERMS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/rewardsConfiguration",
    element: withLayout(rewardsConfigPage)({}),
    requiredActions: ["REWARDS_CONFIGURATION_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/dashboard",
    element: withLayout(DashboardPage)({}),
    requiredActions: ["DASHBOARD_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId/rewards",
    element: withLayout(RewardsPage)({}),
    requiredActions: ["PARTICIPANT_REWARDS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId/journals",
    element: withLayout(JournalsPage)({}),
    requiredActions: ["PARTICIPANT_JOURNALS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/messages",
    element: withLayout(MessagesPage)({}),
    requiredActions: ["CHAT_CONTACTS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/screening-colors",
    element: withLayout(ScreeningColorsPage)({}),
    requiresAuth: false,
    requiredActions: ["COLOR_LIST_VIEW"],
  },
  {
    path: "/settings",
    element: withLayout(SettingsPage)({}),
    requiresAuth: true,
    requiredActions: ["SETTINGS_VIEW"],
  },
  {
    path: "/settings-catalog",
    element: withLayout(SettingsCatalogPage)({}),
    requiresAuth: true,
    requiredActions: ["SETTINGS_CATALOG_VIEW"],
  },
  {
    path: "/settings-values",
    element: withLayout(AccountSettingsPage)({}),
    requiresAuth: true,
    requiredActions: ["ACCOUNT_SETTINGS_VIEW"],
  },
  {
    path: "/403",
    element: withLayout(Forbidden, true)({}),
    requiresAuth: false,
  },
  {
    path: "/404",
    element: withLayout(NotFound, true)({}),
    requiresAuth: false,
  },
];

export const isKnownRoute = (path: string): boolean => {
  const routePatterns = routesConfig.flatMap((route: RouteItem) => {
    const childPatterns =
      route.children
        ?.filter((child) => child.element)
        .map((child) => `${route.path.replace(/\/$/, "")}/${child.path}`) || [];

    return route.element ? [route.path, ...childPatterns] : childPatterns;
  });

  const regexPatterns = routePatterns.map(
    (routePattern) => new RegExp(`^${routePattern.replace(/:\w+/g, "\\w+")}$`)
  );

  return regexPatterns.some((regex) => regex.test(path));
};
